import React from 'react'

//* components
import Button from '../../../src/components/global/button'
import Image from '../../../src/components/global/image'
import { BlogQuoteWHeadshot } from '../../../src/components/global/quote/style'
import Typography from '../../../src/components/global/typography'
import VideoPlayer from '../../../src/components/global/videoPlayer'
import { InlineAuthor } from '../../../src/components/page/resources/formPage'

//* utils
import ImgUtil from '../../../src/utils/img.utils'
import { internalLinkHandler } from '../../../src/utils/link.utils'

export const getWrapperImage = (fields: any) => {
  const url = fields?.image?.fields?.file?.url
  const altText = fields?.altText || ''

  const imageData = {
    imageFile: {
      title: altText,
      file: {
        url: `https://${url}`,
      },
    },
  }

  return <Image enlargeable={fields?.enlargeable} data={imageData} outerPage={''} />
}

const generateByline = (author: { name: string; title: string; company: string }) => {
  let byline = ''

  if (author.name) {
    byline += author.name

    if (author.title) {
      byline += ` · ${author.title}`

      if (author.company) {
        byline += ` at ${author.company}`
      }
    }
  }

  return byline
}

export const getQuote = (fields: any) => {
  const imgUrl = fields.image?.fields?.image?.fields?.file?.url
  const quoteData = {
    image: {
      url: imgUrl ? ImgUtil.getResizedImageUrl(imgUrl, `width=250`) : '/',
      alt: fields.image?.fields?.altText || 'Quote Author Headshot',
    },
    body: fields.body || '',
    author: {
      name: fields.authorName || '',
      title: fields.authorTitle || '',
      company: fields.company || '',
    },
  }

  const byline = generateByline(quoteData.author)
  return (
    <BlogQuoteWHeadshot>
      {quoteData.body && <Typography type="lead">{quoteData.body}</Typography>}

      <div className="BlockQuote__Headshot-Img">
        <img
          className="BlockQuote__Headshot-Img-File"
          alt={quoteData.image.alt}
          src={quoteData.image.url}
        />
      </div>

      <Typography className="BlockQuote__Byline" type="label">
        {byline}
      </Typography>
    </BlogQuoteWHeadshot>
  )
}

export const getWrapperVideo = (fields: any) => {
  if (!fields) return null
  const videoData = {
    altText: fields.altText || '',
    animationUrl: fields.animationFile?.file?.url || '',
    imageUrl: fields.image?.fields?.file?.url || '',
    id: '',
    type: '',
    url: '',
    start: fields.start || '',
    imageOverlay: fields?.imageOverlay || '',
  }

  if (fields.youtubeId) {
    videoData.id = fields.youtubeId
    videoData.type = 'youtube'
  } else if (fields.vimeoId) {
    videoData.id = fields.vimeoId
    videoData.type = 'vimeo'
  } else if (fields.videoUrl) {
    videoData.url = fields.videoUrl
    videoData.type = 'html-video'
  } else if (fields.tencentVideoUrl) {
    videoData.url = fields.tencentVideoUrl
    videoData.type = 'tencent-video'
  }

  if (videoData.animationUrl) {
    const last = videoData.animationUrl.lastIndexOf('.')

    return videoData.animationUrl.substring(last).includes('.mp4') ? (
      <video src={videoData.animationUrl} playsInline />
    ) : (
      <img alt={videoData.altText} src={videoData.animationUrl} loading="lazy" />
    )
  } else if ((videoData.id || videoData.url) && videoData.imageUrl) {
    return (
      <VideoPlayer
        width={'100%'}
        height="429"
        videoId={videoData.id}
        videoUrl={videoData.url}
        type={videoData.type}
        img={videoData.imageUrl}
        backgroundAltText={videoData.altText}
        start={videoData.start}
        imageOverlay={videoData.imageOverlay}
      />
    )
  } else {
    return null
  }
}

export const getCustomerDetails = (fields: any) => {
  const image = {
    url: fields.colorLogo?.fields?.image?.fields?.file?.url || '',
    alt: fields.colorLogo.fields.altText,
  }

  if (image.url && image.alt) {
    return <img src={image.url} alt={image.alt} />
  } else {
    return null
  }
}

export const getBio = (fields: any) => {
  const imageUrl = fields.headshot?.fields?.image?.fields?.file?.url || ''
  const bioData = {
    image: {
      url: imageUrl ? ImgUtil.getResizedImageUrl(imageUrl, `width=250`) : '/',
      alt: fields.headshot?.fields?.altText || 'Headshot image',
    },
    author: {
      firstName: fields.firstName || '',
      lastName: fields.lastName || '',
      title: fields.role || '',
      company: fields.company || '',
    },
  }

  const authorFullName = bioData.author.firstName + ' ' + bioData.author.lastName
  let byline = ''

  if (bioData.author.title && bioData.author.company) {
    byline = `${bioData.author.title} at ${bioData.author.company}`
  } else if (bioData.author.title) {
    byline = bioData.author.title
  } else if (bioData.author.company) {
    byline = bioData.author.company
  }

  return (
    <InlineAuthor>
      <div className="InlineAuthor__Headshot-Img">
        <img
          className="InlineAuthor__Headshot-Img-File"
          alt={bioData.image.alt}
          src={bioData.image.url}
        />
      </div>

      <div className="InlineAuthor__Information">
        <Typography className="InlineAuthor__Name" type="label">
          {authorFullName}
        </Typography>

        <Typography className="InlineAuthor__Byline" type="caption">
          {byline}
        </Typography>
      </div>
    </InlineAuthor>
  )
}

export const getTopicCta = (fields: any) => {
  const externalLink = fields?.externalUrl || ''
  const internalLink = fields?.internalLink ? internalLinkHandler(fields.internalLink) : ''
  const text = fields?.text || ''
  const link = externalLink || internalLink || ''

  if (link) {
    return (
      <div className="Inline-CTA-Container">
        <Button href={link} icon={true} text={text} type="primary" size="small" theme="light" />
      </div>
    )
  } else {
    return null
  }
}

export const getIconLink = (fields: any) => {
  const iconLinkData = {
    url: fields.url || '/',
    text: fields.ctaText || '',
    icon: {
      url: fields.icon.fields.image.fields.file.url || '',
      alt: fields.icon.fields.altText || 'Button Icon',
    },
  }

  return (
    <Button
      href={iconLinkData.url}
      icon={true}
      iconFile={iconLinkData.icon.url}
      text={iconLinkData.text}
      iconAlignment="left"
      type="tertiary"
      size="small"
      theme="light"
      iconFileAlt={iconLinkData.icon.url}
      className="EmbeddedIconButton"
    />
  )
}
